import React, { useEffect, useMemo, useState } from 'react';
import { AppAutocomplete } from '../ui';
import { useKkpClientSearch } from '../../hooks/useKkpClientSearch';
import kkpService from '../../services/kkpService';

const EMPTY_ENTREPRISE = {
  id: null,
  nom: '',
  client_label: null,
  commercial_id: null,
  commercial_nom: null,
  commercial_email: null,
  commercial_emails: [],
  adresse: null,
  cp: null,
  ville: null,
  telephone: null,
  email: null,
};

const KkpEntrepriseAutocomplete = ({
  value,
  onSelect,
  onResolvingChange,
  className = '',
  inputClassName = '',
  size,
  placeholder = 'Rechercher une entreprise Dolibarr…',
}) => {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const { suggestions, loading } = useKkpClientSearch(inputValue, { dolibarrOnly: true });
  const dolibarrSuggestions = useMemo(
    () => (suggestions ?? []).filter((item) => Number(item?.id) > 0 && !item?.entreprise),
    [suggestions],
  );

  const setResolvingState = (next) => {
    setResolving(next);
    onResolvingChange?.(next);
  };

  const handleSelect = async (entreprise) => {
    const thirdpartyId = Number(entreprise?.id) || 0;
    setInputValue(entreprise.nom ?? '');
    if (thirdpartyId <= 0) {
      onSelect?.({
        ...EMPTY_ENTREPRISE,
        nom: String(entreprise?.nom ?? '').trim() || null,
      });
      return;
    }

    setResolvingState(true);
    try {
      const detail = await kkpService.getDolibarrClient(thirdpartyId);
      const companyName = String(detail?.nom ?? entreprise.nom ?? '').trim();
      const contactName = String(detail?.client_label ?? '').trim();
      const commercialId = Number(detail?.commercial_id) > 0 ? Number(detail.commercial_id) : null;
      onSelect?.({
        id: detail?.id ?? thirdpartyId,
        nom: companyName || entreprise.nom,
        client_label: contactName || null,
        commercial_id: commercialId,
        commercial_nom: String(detail?.commercial_nom ?? '').trim() || null,
        commercial_email: String(detail?.commercial_email ?? '').trim() || null,
        commercial_emails: Array.isArray(detail?.commercial_emails) ? detail.commercial_emails : [],
        adresse: detail?.adresse ?? null,
        cp: detail?.cp ?? null,
        ville: detail?.ville ?? null,
        telephone: detail?.telephone ?? null,
        email: detail?.email ?? null,
      });
    } catch {
      onSelect?.({
        ...EMPTY_ENTREPRISE,
        id: thirdpartyId,
        nom: String(entreprise.nom ?? '').trim() || null,
      });
    } finally {
      setResolvingState(false);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim() === '' && value) {
      onSelect?.(EMPTY_ENTREPRISE);
    }
  };

  return (
    <AppAutocomplete
      className={`kkp-entreprise-autocomplete ${className}`.trim()}
      inputClassName={inputClassName}
      size={size}
      value={inputValue}
      onChange={setInputValue}
      onSelect={handleSelect}
      onBlur={handleBlur}
      items={dolibarrSuggestions}
      loading={loading || resolving}
      getItemKey={(entreprise) => entreprise.id}
      renderItem={(entreprise) => (
        <>
          {entreprise.nom}
          {entreprise.siret && (
            <span className="text-secondary-light ms-2 text-sm">
              SIRET {entreprise.siret}
            </span>
          )}
        </>
      )}
      placeholder={placeholder}
      emptyMessage="Aucune entreprise Dolibarr trouvée — vérifiez l'orthographe ou lancez une synchro"
      loadingLabel="Recherche Dolibarr…"
    />
  );
};

export default KkpEntrepriseAutocomplete;
